import { Select } from '@mui/material'
import { useState } from 'react'
import { API_USER_CHAT_IMAGES } from '../../../../constants/endpoints'
import { IMAGE_CATEGORIES } from '../../../../mfcLogic/api/chatImages'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { StyledMenuItem } from '../../../common/MenuItem'
import { Status } from '../../../common/StatusSnackbar'
import Tooltip from '../../Tooltip'
import {
    ButtonContainer,
    HeaderText,
    LabelText,
    Row,
    Section,
    StyledButton,
    StyledSelect,
    StyledTextField,
    isValidUrl,
} from './Shared'

// Filter out categories that shouldn't be available for upload
const uploadableCategories = Object.entries(IMAGE_CATEGORIES).filter(
    ([category, _]) =>
        category !== 'profilePics' &&
        category !== 'personalLibrary' &&
        category !== 'recentlyUsed',
)

const InfoTooltip = () => {
    return (
        <Tooltip>
            <div css={{ fontSize: 11 }}>
                After your image is approved, it can be used by anyone and
                cannot be removed or modified in the future. Maximum file size
                is 8mb; do not submit copyrighted images. Please note that it
                may take up to 24 hours before people can make use of the new
                chat image.
            </div>
        </Tooltip>
    )
}

interface ImageLibrarySubmissionProps {
    setStatus: (status: Status) => void
}

const ImageLibrarySubmission = (props: ImageLibrarySubmissionProps) => {
    const { setStatus } = props
    const [libraryImageUrl, setLibraryImageUrl] = useState('')
    const [shortcutName, setShortcutName] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [selectedCategoryKey, setSelectedCategoryKey] = useState(
        uploadableCategories[6][0], // default to 'general'
    )
    const token = useAppStateSelector(AppStateType.SESSION, 'token')

    const handleCategoryChange = (event) => {
        setSelectedCategoryKey(event.target.value)
    }

    const showStatus = (message: string, error = false) => {
        setStatus({ error, message })
    }

    const handleSubmitImageToLibrary = async () => {
        if (!libraryImageUrl.trim()) {
            showStatus('Please enter an image URL', true)
            return
        }

        if (!isValidUrl(libraryImageUrl)) {
            showStatus('Please enter a valid URL', true)
            return
        }

        if (!shortcutName.trim()) {
            showStatus('Please enter a proposed shortcut name', true)
            return
        }

        const shortcutRegex = /^[a-z0-9]+$/
        if (!shortcutRegex.test(shortcutName)) {
            showStatus(
                'Shortcut must contain only lowercase letters and numbers',
                true,
            )
            return
        }

        const categoryId = IMAGE_CATEGORIES[selectedCategoryKey].id
        if (!categoryId) {
            showStatus('Invalid category selected', true)
            return
        }

        setSubmitting(true)

        try {
            const params = new URLSearchParams()
            params.append('category', categoryId.toString())
            params.append('image_url', libraryImageUrl)
            params.append('proposed_shortcut', shortcutName)
            params.append('token', token)
            const paramsString = params.toString()

            const apiUrl = `${API_USER_CHAT_IMAGES}/client/submitUserChatImage`

            const response = await fetch(`${apiUrl}?${paramsString}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: paramsString,
                credentials: 'include',
            })

            const resp = await response.json()

            if (resp.err || resp.errmsg?.message) {
                const errorMessage =
                    resp.errmsg?.message ||
                    'Please contact support. Error #M98726'
                throw new Error(errorMessage)
            }

            if (resp.result?.message) {
                showStatus(resp.result.message)
                setLibraryImageUrl('')
                setShortcutName('')
            } else {
                throw new Error('Please contact support. Error #M98726')
            }
        } catch (error) {
            console.error('Error submitting image to library:', error)
            showStatus(`Submission failed: ${error.message}`, true)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Section>
            <Row>
                <HeaderText>
                    Submit an image to the Chat Image Library
                </HeaderText>
                <InfoTooltip />
            </Row>

            <Row>
                <LabelText>Choose a Category:</LabelText>
                <StyledSelect fullWidth variant="outlined">
                    <Select
                        value={selectedCategoryKey}
                        onChange={handleCategoryChange}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        {uploadableCategories.map(([key, category]) => (
                            <StyledMenuItem key={key} value={key}>
                                {category.label}
                            </StyledMenuItem>
                        ))}
                    </Select>
                </StyledSelect>
            </Row>

            <Row>
                <LabelText>Select Image URL:</LabelText>
                <StyledTextField
                    placeholder="https://..."
                    variant="outlined"
                    value={libraryImageUrl}
                    onChange={(e) => setLibraryImageUrl(e.target.value)}
                />
            </Row>

            <Row>
                <LabelText>Proposed Shortcut:</LabelText>
                <StyledTextField
                    placeholder="lowercase letters and numbers"
                    variant="outlined"
                    value={shortcutName}
                    onChange={(e) => setShortcutName(e.target.value)}
                />
            </Row>

            <ButtonContainer>
                <StyledButton
                    disableElevation
                    onClick={handleSubmitImageToLibrary}
                    disabled={
                        submitting ||
                        !libraryImageUrl.trim() ||
                        !shortcutName.trim()
                    }
                >
                    {submitting ? 'SUBMITTING...' : 'SUBMIT IMAGE'}
                </StyledButton>
            </ButtonContainer>
        </Section>
    )
}

export default ImageLibrarySubmission
